import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';
import { getItemAssetPairs, ItemAssetPairs } from './ItemAssetPairs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreditNoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const fetch = async () => {
      try {
        const [docRes, prodRes, unitRes, taxRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/credit-notes/${id}`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders())
        ]);
        setDoc(docRes.data);
        setProducts(prodRes.data);
        setUnits(unitRes.data);
        setTaxes(taxRes.data);
      } catch (err) { setError('Failed to load credit note'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id]);

  const getName = (list, id, field = 'name') => list.find(i => i.id === id)?.[field] || '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm"><AlertCircle className="h-5 w-5" />{error}</div>;
  if (!doc) return null;

  const exportConfig = {
    title: 'Sale Return',
    number: doc.return_number,
    metadata: [
      { label: 'Return Number', value: doc.return_number || '-' },
      { label: 'Date', value: formatDate(doc.date) },
      { label: 'Invoice Number', value: doc.invoice_number || '-' },
      { label: 'Invoice Date', value: formatDate(doc.invoice_date) },
      { label: 'Customer', value: doc.customer_name || '-' },
      { label: 'Company', value: doc.company_name || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' },
      { label: 'Product', value: item => getName(products, item.product_id) },
      { label: 'Item ID / Asset ID', value: item => getItemAssetPairs({ item_details: item.item_details, itemIds: item.item_ids, assetIds: item.asset_ids }).map(pair => `${pair.item_id || '-'} / ${pair.asset_id || '-'}`).join('\n') || '-' },
      { label: 'Description', value: item => item.description || '-' },
      { label: 'MRP', value: item => Number(item.mrp || 0), type: 'number' },
      { label: 'Qty', value: item => Number(item.quantity || 0), type: 'number' },
      { label: 'Unit', value: item => getName(units, item.unit_id) },
      { label: 'Price/Unit', value: item => Number(item.price_per_unit || 0), type: 'number' },
      { label: 'Disc %', value: item => Number(item.discount_percentage || 0), type: 'number' },
      { label: 'Disc Amt', value: item => Number(item.discount_amount || 0), type: 'number' },
      { label: 'Tax', value: item => item.tax_id ? getName(taxes, item.tax_id) : '-' },
      { label: 'Tax Amt', value: item => Number(item.tax_amount || 0), type: 'number' },
      { label: 'Amount', value: item => Number(item.total_amount || 0), type: 'number' },
    ],
    rows: doc.line_items || [],
    totals: [
      { label: 'Subtotal', value: `INR ${Number(doc.subtotal || 0).toFixed(2)}`, rawValue: Number(doc.subtotal || 0) },
      { label: 'Total', value: `INR ${Number(doc.total || 0).toFixed(2)}`, rawValue: Number(doc.total || 0) },
      { label: 'Utilized', value: `INR ${Number(doc.allocated_amount || 0).toFixed(2)}`, rawValue: Number(doc.allocated_amount || 0) },
      { label: 'Available', value: `INR ${Number(doc.available_amount || 0).toFixed(2)}`, rawValue: Number(doc.available_amount || 0) },
    ],
    shareLines: [
      `Customer: ${doc.customer_name || '-'}`,
      `Date: ${formatDate(doc.date)}`,
      `Total: INR ${Number(doc.total || 0).toFixed(2)}`,
    ],
  };

  return (
    <div data-testid="credit-note-detail-page">
      <button onClick={() => navigate('/admin/sale/return')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors print:hidden">
        <ArrowLeft className="h-4 w-4" /> Back to Credit Notes
      </button>
      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-4">Credit Note Details</h1>
      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="sale-return" /></div>

      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Return Number</span><span className="text-sm font-semibold text-slate-900">{doc.return_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Date</span><span className="text-sm text-slate-900">{formatDate(doc.date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice No.</span><span className="text-sm text-slate-900">{doc.invoice_number || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Invoice Date</span><span className="text-sm text-slate-900">{formatDate(doc.invoice_date)}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Customer</span><span className="text-sm text-slate-900">{doc.customer_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">Company</span><span className="text-sm text-slate-900">{doc.company_name || '-'}</span></div>
          <div><span className="block text-xs font-medium text-slate-500 uppercase">State of Supply</span><span className="text-sm text-slate-900">{doc.state_of_supply || '-'}</span></div>
          {doc.description && <div className="col-span-2"><span className="block text-xs font-medium text-slate-500 uppercase">Description</span><span className="text-sm text-slate-900">{doc.description}</span></div>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 p-4"><span className="block text-xs font-medium text-slate-500 uppercase">Original Amount</span><span className="text-xl font-bold text-slate-900">₹{Number(doc.total || 0).toLocaleString('en-IN')}</span></div>
        <div className="bg-white border border-slate-200 p-4"><span className="block text-xs font-medium text-slate-500 uppercase">Utilized</span><span className="text-xl font-bold text-amber-700">₹{Number(doc.allocated_amount || 0).toLocaleString('en-IN')}</span></div>
        <div className="bg-white border border-slate-200 p-4"><span className="block text-xs font-medium text-slate-500 uppercase">Available Balance</span><span className="text-xl font-bold text-green-700">₹{Number(doc.available_amount || 0).toLocaleString('en-IN')}</span><span className="block text-xs text-slate-500 mt-1">{doc.allocation_status || 'Available'}</span></div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Allocation History</h2>
        </div>
        {(doc.allocations || []).length === 0 ? (
          <p className="px-6 py-5 text-sm text-slate-500">This Credit Note has not been used against a Sale Invoice yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase"><th className="px-4 py-3 text-left">Sale Invoice</th><th className="px-4 py-3 text-left">Allocated On</th><th className="px-4 py-3 text-right">Amount Used</th></tr></thead>
              <tbody>{doc.allocations.map((allocation, index) => (
                <tr key={allocation.id || index} className="border-b border-slate-100">
                  <td className="px-4 py-3"><button onClick={() => navigate(`/admin/sale/invoices/${allocation.reference_id}`)} className="font-semibold text-blue-600 hover:underline">{allocation.reference_label || allocation.reference_id}</button></td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(allocation.allocated_at)}</td>
                  <td className="px-4 py-3 text-right font-semibold">₹{Number(allocation.amount || 0).toLocaleString('en-IN')}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Item ID / Asset ID</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-right">MRP</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3 text-left">Unit</th>
                <th className="px-4 py-3 text-right">Price/Unit</th>
                <th className="px-4 py-3 text-right">Disc %</th>
                <th className="px-4 py-3 text-right">Disc Amt</th>
                <th className="px-4 py-3 text-left">Tax</th>
                <th className="px-4 py-3 text-right">Tax Amt</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{getName(products, li.product_id)}</td>
                  <td className="px-4 py-3"><ItemAssetPairs item_details={li.item_details} itemIds={li.item_ids} assetIds={li.asset_ids} testId={`item-assets-${idx}`} /></td>
                  <td className="px-4 py-3 text-slate-600">{li.description || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.mrp || '-'}</td>
                  <td className="px-4 py-3 text-right">{li.quantity}</td>
                  <td className="px-4 py-3">{getName(units, li.unit_id)}</td>
                  <td className="px-4 py-3 text-right">{li.price_per_unit?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{li.discount_percentage}%</td>
                  <td className="px-4 py-3 text-right">{li.discount_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">{li.tax_id ? getName(taxes, li.tax_id) : '-'}</td>
                  <td className="px-4 py-3 text-right">{li.tax_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-medium">{li.total_amount?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-6">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-medium">₹{(doc.subtotal || 0).toFixed(2)}</span></div>
          {doc.round_off && <div className="flex justify-between text-sm"><span className="text-slate-500">Round Off</span><span>{(doc.round_off_amount || 0) >= 0 ? '+' : ''}{(doc.round_off_amount || 0).toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2"><span>Total</span><span>₹{(doc.total || 0).toLocaleString()}</span></div>
        </div>
      </div>
    </div>
  );
};
